// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngUtilities from "@synd/App/Components/Common/XngUtilities";
import XngZone from "@synd/App/Components/Common/XngZone";
import InsufficientInventoryModal from "@synd/App/Components/Product/InsufficientInventoryModal";
import PreBookModal from "@synd/App/Components/Product/PreBookModal";
import useSize from "@synd/App/Hooks/useSize";
import { CustomAppState } from "@synd/App/Store/Reducers";
import { isCheckoutPage } from "@synd/App/Store/Selectors/CheckoutSelector";
import { getLogoImageWidget } from "@synd/App/Store/Selectors/GeneralSelectors";
import LazyImage, { LazyImageProps } from "@synd/MobiusOverrides/LazyImage";
import { HEADER_HEIGHT_CSS_VAR, PAGE_LAYOUT_SPACING } from "@synd/XDL/constants";
import breakpoints from "@synd/XDL/PreStyle/breakpoints";
import { addScrollbarGutter, removeScrollbarGutter } from "@synd/XDL/Utilities";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Change
    isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    simpleHeader: isCheckoutPage(state) || getCurrentPage(state).type === "CreateAccountPage",
    pathname: getLocation(state).pathname,
    headerRegisteredMenu: (state as CustomAppState).xHeader.registerMenu?.length || 0,
    headerRegisteredDrawers: (state as CustomAppState).xHeader.drawers.length,
    logoImageWidget: getLogoImageWidget(state),
});

// XNG-Change

const padding = (breakpoints as any).paddingValues[0];
const maxPageWidthLg = breakpoints.maxWidths[breakpoints.maxWidths.length - 2];
const maxPageWidthXl = breakpoints.maxWidths[breakpoints.maxWidths.length - 1];

const mapDispatchToProps = {
    dismissRfqUpdates,
    setNavDrawerIsOpen,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    topSection?: InjectableCss;
    middleSection?: InjectableCss;
    middleSectionContainer?: InjectableCss;
    logo?: InjectableCss;
    search?: InjectableCss;
    actions?: InjectableCss;
    secondaryMainNavigation?: InjectableCss;
    mainNavigation?: InjectableCss;
    actionLinks?: InjectableCss;
    checkoutWrapper?: InjectableCss;
    checkoutHelp?: InjectableCss;
    printLogo?: InjectableCss;
    printLogoImage?: LazyImageProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    session,
    toaster,
    history,
    myAccountPageUrl,
    isTabletScreen,
    simpleHeader,
    dismissRfqUpdates,
    pathname,
    setNavDrawerIsOpen,
    headerRegisteredMenu,
    headerRegisteredDrawers,
    // XNG-Changes
    logoImageWidget,
}: Props) => {
    // <XNG-Changes>
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;
    const editMode = useShellContext().isEditing;

    useEffect(() => {
        if (headerHeight) {
            if (typeof document === "undefined") {
                return;
            }
            const cssRoot = document.querySelector(":root") as HTMLElement;
            cssRoot.style.setProperty(HEADER_HEIGHT_CSS_VAR, `${headerHeight + 1}px`);
            cssRoot.style.setProperty(PAGE_LAYOUT_SPACING.lg, `${maxPageWidthLg - padding * 2}px`);
            cssRoot.style.setProperty(PAGE_LAYOUT_SPACING.xl, `${maxPageWidthXl - padding * 2}px`);
        }
    }, [headerHeight]);

    useEffect(() => {
        if (headerRegisteredMenu > 0) {
            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        } else {
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }

        if (headerRegisteredDrawers) {
            document.body.classList.add("drawer-open");
        } else {
            document.body.classList.remove("drawer-open");
        }
    }, [headerRegisteredMenu, headerRegisteredDrawers]);

    useEffect(() => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    }, [pathname]);

    // <XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImageWidget && (
                <StyledWrapper {...styles.printLogo}>
                    <LazyImage
                        src={logoImageWidget.logoImage}
                        altText={translate("Home")}
                        {...styles.printLogoImage}
                        disableLazyLoad={true}
                        height="100%"
                        width="100%"
                    />
                </StyledWrapper>
            )}
            {/* XNG-Changes */}
            <XngUtilities />
            <Page as="header" id="main-header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* XNG-Changes: structure zones */}
                    <StyledWrapper id="header-container" ref={headerRef}>
                        {!simpleHeader && (
                            <>
                                <XngZone
                                    contentId={id}
                                    zoneName="GlobalSiteMessage"
                                    hint="Zone for Global Site Message"
                                />
                                <StyledWrapper {...styles.container}>
                                    <StyledWrapper {...styles.topSection}>
                                        <XngZone hint="Secondary Header" contentId={id} zoneName="SecondaryHeader" />
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.middleSection}>
                                        <StyledWrapper {...styles.middleSectionContainer}>
                                            <StyledWrapper {...styles.search} data-text-selector="search">
                                                <XngZone hint="Search" contentId={id} zoneName="Search" />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.logo} data-text-selector="logo">
                                                <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.actions} data-text-selector="actions">
                                                <StyledWrapper {...styles.actionLinks}>
                                                    <XngZone hint="Links" contentId={id} zoneName="ActionLinks" />
                                                </StyledWrapper>
                                                <XngZone hint="Actions" contentId={id} zoneName="Actions" />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    {!isTabletScreen && (
                                        <StyledWrapper
                                            {...styles.secondaryMainNavigation}
                                            data-test-selector="secondary-main-navigation"
                                        >
                                            <XngZone
                                                hint="Secondary Main Navigation"
                                                contentId={id}
                                                zoneName="SecondaryMainNavigation"
                                            />
                                        </StyledWrapper>
                                    )}
                                    <StyledWrapper {...styles.mainNavigation} data-test-selector="main-navigation">
                                        <XngZone hint="Main Navigation" contentId={id} zoneName="MainNavigation" />
                                    </StyledWrapper>
                                </StyledWrapper>
                            </>
                        )}
                        {simpleHeader && (
                            <StyledWrapper {...styles.checkoutWrapper}>
                                <StyledWrapper {...styles.logo} data-text-selector="logo">
                                    <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <StyledWrapper {...styles.checkoutHelp}>
                                    <XngZone hint="Checkout Help" contentId={id} zoneName="CheckoutHelp" />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        {editMode && <XngZone hint="Checkout Help" contentId={id} zoneName="CheckoutHelp" />}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
                {/* XNG-Change */}
                <PreBookModal />
                <InsufficientInventoryModal />
            </Page>
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
